import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import Icon from '~/components/Icon/Icon';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import Modal from '~/components/Modal/Modal';
import { MIN_AGE } from '~/components/ModalBirthDate/constants';
import { useGlobal } from '~/hooks/useGlobal';

import { cartStore, settingStore, userStore } from '~/stores';

import styles from './ModalStockChanged.module.scss';
import StockProductCard from './StockProductCard';

const ModalStockChanged = () => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();
  const { cartStockChangesStore } = cartStore;

  const isAgeRestriction = cartStockChangesStore.isAgeRestriction;

  const renderProductCards = () => {
    if (!isMobile) {
      return cartStockChangesStore.unavailableCartItems.map((item) => (
        <StockProductCard
          key={item.cartItem.id}
          product={item.cartItem}
          stockInfo={item.error}
          bundleStockInfo={item.bundleError}
          isAgeRestriction={isAgeRestriction}
        />
      ));
    }

    return (
      <Carousel
        opts={{
          align: 'start' as const,
          slidesToScroll: 1,
          containScroll: 'trimSnaps' as const,
        }}
        className={styles.carousel}
        dir={userStore.dir}
        key={userStore.dir}
      >
        <CarouselContent>
          {cartStockChangesStore.unavailableCartItems.map((item) => (
            <CarouselItem key={item.cartItem.id} className={styles.slide}>
              <StockProductCard
                product={item.cartItem}
                stockInfo={item.error}
                bundleStockInfo={item.bundleError}
                className={styles.slideProduct}
                isAgeRestriction={isAgeRestriction}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    );
  };

  return (
    <Modal
      isShow={
        cartStockChangesStore.isOpen &&
        ['/cart', '/checkout'].includes(pathname)
      }
      className={styles.modalStockChanged}
      onBackdropDismiss={() => void 0}
    >
      <header
        className={classes([
          styles.header,
          isAgeRestriction && styles.ageVeriffHeader,
        ])}
      >
        <div className={styles.header__titleWr}>
          <span
            className={classes([isAgeRestriction && styles.ageVeriffTitle])}
          >
            {t(
              isAgeRestriction
                ? 'ageRestriction:calculateErrorTitle'
                : 'phrases:availableStockChanged',
            )}
          </span>
          {isMobile && (
            <span
              className={styles.close}
              role="button"
              onClick={() =>
                cartStockChangesStore.updateCartAndCloseNotifierModal()
              }
            >
              <Icon type="close" size={isAgeRestriction ? 24 : 32} />
            </span>
          )}
        </div>
        {isAgeRestriction && (
          <p className={styles.ageRestrictionCaption}>
            {t('ageRestriction:calculateErrorDescription', {
              minAge:
                settingStore.getMinAgeForItems(cartStore.selectedCartItems) ||
                settingStore.maxMinAge ||
                MIN_AGE,
            })}
          </p>
        )}
      </header>
      <div className={styles.root}>{renderProductCards()}</div>
      <footer className={styles.footer}>
        <button
          className={classes(['button _primary', styles.okButton])}
          onClick={() =>
            cartStockChangesStore.updateCartAndCloseNotifierModal()
          }
        >
          {t(isAgeRestriction ? 'ageRestriction:removeItems' : 'ok')}
        </button>
      </footer>
    </Modal>
  );
};

export default observer(ModalStockChanged);
